import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Switch,
  CircularProgress,
  IconButton,
  TextField,
  MenuItem,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Lock, LockOpen, Refresh, ChevronLeft, ChevronRight } from '@mui/icons-material';
import expensesService from '../../services/expensesService';
import { format } from 'date-fns';

const AccountingLock = () => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;

  const fetchPeriods = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        page_size: rowsPerPage
      };
      const data = await expensesService.getAccountingPeriods(params);
      setPeriods(data.results || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      setError('Failed to fetch accounting periods');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  const handleToggleLock = async (period) => {
    try {
      if (period.is_locked) {
        await expensesService.unlockPeriod(period.id);
      } else {
        await expensesService.lockPeriod(period.id);
      }
      fetchPeriods();
    } catch (err) {
      setError('Failed to update lock status');
    }
  };

  const handleRefresh = () => {
    setPage(0);
    fetchPeriods();
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const columns = [
    {
      field: 'period',
      headerName: 'Period',
      minWidth: 200,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2">
          {format(new Date(params.row.year, params.row.month - 1), 'MMMM yyyy')}
        </Typography>
      )
    },
    {
      field: 'is_locked',
      headerName: 'Status',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        params.value ? (
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
            <Lock fontSize="small" sx={{ mr: 1 }} /> Locked
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
            <LockOpen fontSize="small" sx={{ mr: 1 }} /> Open
          </Box>
        )
      )
    },
    {
      field: 'locked_by_name',
      headerName: 'Locked By',
      minWidth: 200,
      flex: 1,
      valueFormatter: (params) => params.value || '-'
    },
    {
      field: 'locked_at',
      headerName: 'Locked At',
      minWidth: 200,
      flex: 1,
      valueFormatter: (params) => params.value ? format(new Date(params.value), 'MMM dd, yyyy HH:mm') : '-'
    },
    {
      field: 'actions',
      headerName: 'Action',
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Switch
          checked={params.row.is_locked}
          onChange={() => handleToggleLock(params.row)}
          color="error"
        />
      )
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default', 
      px: { xs: 0.75, sm: 1, md: 1.5 }, 
      py: { xs: 2, sm: 2.5, md: 3 }, 
      maxWidth: '95vw', 
      overflowX: 'hidden' 
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          Accounting Period Management
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Refresh />} 
          onClick={handleRefresh}
          sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
        >
          Refresh
        </Button>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, height: 400, alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                rows={periods}
                columns={columns}
                getRowId={(row) => row.id}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 800,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#DC143C',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Rows per page
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AccountingLock;
